import { useState } from 'react';
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { Icon, RefreshIcon, type IconName } from './icon';

/**
 * Shared presentational components.
 *
 * Small on purpose: the design system lives in CSS tokens, and these wrappers
 * exist only to keep class names and accessibility details in one place.
 */

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  block?: boolean;
  loading?: boolean;
}

/*
 * The loading geometry lives here rather than in `base.css` because it is a
 * correctness property of this component, not a theme choice: the button must
 * not move, whatever stylesheet it lands in.
 *
 * `display: contents` keeps the children as flex items of the button itself, so
 * the gap between a leading glyph and its label survives the wrapper; the
 * inherited `visibility` is what hides them without giving up their space.
 */
const BUTTON_LABEL: CSSProperties = { display: 'contents' };
const BUTTON_LABEL_LOADING: CSSProperties = { display: 'contents', visibility: 'hidden' };
const BUTTON_SPINNER_SLOT: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
};

export function Button({
  variant = 'primary',
  block,
  loading,
  children,
  className,
  disabled,
  style,
  ...rest
}: ButtonProps) {
  const classes = [
    'button',
    variant !== 'primary' ? `button--${variant}` : '',
    block ? 'button--block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      className={classes}
      style={{ position: 'relative', ...style }}
      disabled={disabled || loading}
      aria-busy={loading}
      {...rest}
    >
      {/*
       * The label does not move when `loading` flips.
       *
       * A spinner in the flex flow pushes a centred label sideways by half its
       * own width plus the gap — a 13px jump at the exact moment the customer
       * is looking at the button and about to press it again. So the spinner is
       * laid *over* the button and the label is only made invisible: the box,
       * the label and the button's width all stay exactly where they were.
       */}
      <span style={loading ? BUTTON_LABEL_LOADING : BUTTON_LABEL}>{children}</span>
      {loading ? (
        <span style={BUTTON_SPINNER_SLOT}>
          <Icon name="spinner" size={18} className="button__spinner" />
        </span>
      ) : null}
    </button>
  );
}

/**
 * A grouped surface: one card per section, rows inside it.
 *
 * `padded` is for prose; leave it off when the children are rows, which bring
 * their own padding and hairlines.
 */
export function Group({
  children,
  padded,
  className,
}: {
  children: ReactNode;
  padded?: boolean;
  className?: string;
}) {
  return (
    <div className={[padded ? 'card' : 'group', className ?? ''].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}

/** Kept for the admin panel, which is not part of this redesign. */
export function Card({
  children,
  onClick,
  className,
  ariaLabel,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  ariaLabel?: string;
}) {
  const classes = ['card', className ?? ''].filter(Boolean).join(' ');

  if (onClick) {
    return (
      <button type="button" className={classes} onClick={onClick} aria-label={ariaLabel}>
        {children}
      </button>
    );
  }

  return <div className={classes}>{children}</div>;
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="section-label">{children}</p>;
}

/**
 * An iOS inset grouped form: one surface, field rows separated by hairlines.
 *
 * The group *is* the box. A field inside it must not bring its own — that is
 * how the old support screen ended up as a card full of lozenges, each control
 * drawing a second border inside a border. Pass bare `<input>` / `<textarea>`
 * elements, without `.field` or `.input`; `base.css` styles them through
 * `.formgroup__row`.
 */
export function FormGroup({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={['formgroup', className ?? ''].filter(Boolean).join(' ')}>{children}</div>;
}

export interface FormRowProps {
  readonly label: ReactNode;
  /** The `id` of the control this row labels. `id` is accepted as an alias. */
  readonly htmlFor?: string;
  readonly id?: string;
  readonly children: ReactNode;
  /** Label above the control instead of beside it — for a multi-line field. */
  readonly stacked?: boolean;
  readonly className?: string;
}

/**
 * One field row inside a `FormGroup`.
 *
 * The label is a real `<label for>`, so tapping it focuses the control and the
 * control is announced by name. Without an id there is nothing to bind to, and
 * a `<label>` that points at nothing is worse than no label element at all — so
 * that case degrades to a plain span rather than shipping a broken `for`.
 */
export function FormRow({ label, htmlFor, id, children, stacked, className }: FormRowProps) {
  const controlId = htmlFor ?? id;
  const classes = ['formgroup__row', stacked ? 'formgroup__row--stacked' : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      {controlId ? (
        <label className="formgroup__label" htmlFor={controlId}>
          {label}
        </label>
      ) : (
        <span className="formgroup__label">{label}</span>
      )}
      {children}
    </div>
  );
}

/**
 * The small tertiary paragraph that goes *under* a group.
 *
 * Explanatory text is not a warning: it needs no box, no tint and no glyph. Use
 * this wherever `Notice` was carrying a plain sentence, and keep `Notice` for
 * the cases that genuinely interrupt.
 */
export function Footnote({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={['footnote', className ?? ''].filter(Boolean).join(' ')}>{children}</p>;
}

export interface RowProps {
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  readonly icon?: IconName;
  readonly trailing?: ReactNode;
  readonly onClick?: () => void;
  /** Renders a tick rather than a chevron, for a choice within a group. */
  readonly selected?: boolean;
  readonly chevron?: boolean;
  readonly disabled?: boolean;
}

/**
 * One line in a group.
 *
 * A real `<button>` when it does something and a `<div>` when it does not — a
 * clickable div is invisible to a keyboard and announces nothing.
 */
export function Row({
  title,
  subtitle,
  icon,
  trailing,
  onClick,
  selected,
  chevron,
  disabled,
}: RowProps) {
  const body = (
    <>
      {icon ? (
        <span className="rowitem__icon">
          <Icon name={icon} size={20} />
        </span>
      ) : null}
      <span className="rowitem__body">
        <span className="rowitem__title">{title}</span>
        {subtitle ? <span className="rowitem__subtitle">{subtitle}</span> : null}
      </span>
      {trailing ? <span className="rowitem__trailing">{trailing}</span> : null}
      {selected ? (
        <span className="rowitem__check">
          {/*
           * On a button the state is already announced by `aria-pressed`, so
           * the tick is decoration. On a plain row there is nothing else to
           * announce it, and a checkmark only a sighted user can read is not a
           * state — it is an unlabelled picture.
           */}
          <Icon name="check" size={18} {...(onClick ? {} : { title: 'Выбрано' })} />
        </span>
      ) : null}
      {chevron && !selected ? (
        <span className="rowitem__chevron">
          <Icon name="chevron" size={17} />
        </span>
      ) : null}
    </>
  );

  const className = ['rowitem', icon ? 'rowitem--inset' : ''].filter(Boolean).join(' ');

  if (!onClick) return <div className={className}>{body}</div>;

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
      /*
       * `aria-pressed` only where `selected` was actually supplied: a row that
       * navigates is not a toggle that happens to be off, and announcing it as
       * one is a lie. A one-of-N picker would strictly want radio semantics,
       * but that needs a `role="radiogroup"` wrapper at the call site — noted
       * rather than faked here, because a lone radio is worse than a toggle.
       *
       * `aria-disabled` rides along with the real `disabled` so the row can be
       * styled as unavailable; `:disabled` alone leaves it looking live.
       */
      {...(selected !== undefined ? { 'aria-pressed': selected } : {})}
      {...(disabled ? { 'aria-disabled': true } : {})}
    >
      {body}
    </button>
  );
}

export function StatusPill({
  children,
  tone = 'neutral',
  icon,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'accent';
  icon?: IconName;
}) {
  return (
    <span className={`pill${tone === 'neutral' ? '' : ` pill--${tone}`}`}>
      {/* Never colour alone: the glyph carries the same meaning. */}
      {icon ? <Icon name={icon} size={14} /> : null}
      {children}
    </span>
  );
}

/** Kept for the admin panel. */
export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'accent';
}) {
  return <span className={`badge${tone === 'neutral' ? '' : ` badge--${tone}`}`}>{children}</span>;
}

/** Loading placeholder sized to the content it replaces, to avoid layout shift. */
export function Skeleton({ height = 20, width = '100%' }: { height?: number; width?: string }) {
  return <div className="skeleton" style={{ height, width }} aria-hidden="true" />;
}

/**
 * Nothing here yet, and the one thing to do about it.
 *
 * One state, one action — `action` is the slot, and it is the *only* slot. The
 * home screen used to render a call to action in here and a second one right
 * underneath it, which reads as two different offers rather than one; if a
 * screen is about to put a button next to an `EmptyState`, that button belongs
 * in `action` instead.
 */
export function EmptyState({
  icon = 'inbox',
  title,
  text,
  action,
}: {
  icon?: IconName;
  title: string;
  text?: string;
  action?: ReactNode;
}) {
  return (
    <div className="state" role="status">
      <span className="state__icon">
        <Icon name={icon} size={32} />
      </span>
      <p className="state__title">{title}</p>
      {text ? <p className="state__text">{text}</p> : null}
      {action ? <div className="state__action">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  title,
  text,
  onRetry,
  retryLabel = 'Повторить',
}: {
  title: string;
  text?: string;
  onRetry?: () => void;
  retryLabel?: string;
}) {
  return (
    <div className="state" role="alert">
      <span className="state__icon state__icon--error">
        <Icon name="alert" size={32} />
      </span>
      <p className="state__title">{title}</p>
      {text ? <p className="state__text">{text}</p> : null}
      {onRetry ? (
        <Button variant="secondary" onClick={onRetry}>
          <RefreshIcon size={16} />
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Something the customer has to deal with before going on.
 *
 * An explanatory sentence is not one of those — use `Footnote`. The alert glyph
 * is earned by `warning` and `danger` and by nothing else: an alarm mark beside
 * a neutral line of prose is noise, and enough of it is why the support screen
 * read as cluttered.
 */
export function Notice({
  children,
  tone = 'info',
  icon,
}: {
  children: ReactNode;
  tone?: 'info' | 'warning' | 'danger';
  /** Overrides the tone's glyph; pass one to give an `info` notice a mark. */
  icon?: IconName;
}) {
  const glyph: IconName | undefined = icon ?? (tone === 'info' ? undefined : 'alert');
  return (
    <div className={`notice${tone === 'info' ? '' : ` notice--${tone}`}`}>
      {glyph ? (
        <span className="notice__icon">
          <Icon name={glyph} size={16} />
        </span>
      ) : null}
      <span>{children}</span>
    </div>
  );
}

export function SearchField({
  value,
  onChange,
  onSubmit,
  placeholder,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
  label: string;
}) {
  return (
    <form
      className="search"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <Icon name="search" size={18} />
      <input
        className="search__input"
        type="search"
        value={value}
        aria-label={label}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
      {value ? (
        <button
          type="button"
          className="search__clear"
          aria-label="Очистить поиск"
          onClick={() => {
            onChange('');
            onSubmit();
          }}
        >
          <Icon name="close" size={16} />
        </button>
      ) : null}
    </form>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="tablist" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          className="segmented__item"
          aria-selected={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* --- the home screen -------------------------------------------------------- */

export interface BannerProps {
  readonly title: ReactNode;
  /** The line under the title. Absent or `null` renders nothing: the content
      system carries banners that are a headline and nothing else. */
  readonly text?: ReactNode;
  readonly onClick?: () => void;
}

/**
 * A promo card, sitting between the store name and the services.
 *
 * Quiet on purpose: one surface, two lines of text, no picture and no tint.
 * What it replaces was a gradient with its own call to action inside it, and
 * two offers stacked above the fold is how a shop reads as an advert rather
 * than as a shop.
 *
 * A `<div>` when it has nowhere to go. A button that does nothing still takes
 * focus and still announces itself as pressable, which is a promise to everyone
 * who reaches it that the card cannot keep.
 */
export function Banner({ title, text, onClick }: BannerProps) {
  const body = (
    <>
      <span className="banner__title">{title}</span>
      {text ? <span className="banner__text">{text}</span> : null}
    </>
  );

  if (!onClick) return <div className="banner">{body}</div>;

  return (
    <button type="button" className="banner" onClick={onClick}>
      {body}
    </button>
  );
}

/**
 * The services grid.
 *
 * Thin on purpose — the geometry is CSS. It exists so that the class name has
 * exactly one spelling in the app; any element carrying `.service-grid` is
 * equivalent to it.
 */
export function ServiceGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={['service-grid', className ?? ''].filter(Boolean).join(' ')}>{children}</div>
  );
}

/** Letters and digits. Everything else in a title is punctuation or an emoji. */
const MONOGRAM_CHAR = /[\p{L}\p{N}]/u;

/**
 * The mark for a service that has no picture.
 *
 * The initial of a one-word name, the two initials of a longer one — the shape
 * of an avatar, which reads as a decision someone made. The alternative is one
 * generic glyph repeated the length of the grid, and a wall of identical
 * placeholders is precisely what makes a live shop look unfinished.
 *
 * Leading emoji and punctuation are stepped over rather than shown: an owner
 * who types «🎧 Spotify» into the row means the mark to be S.
 */
function monogramOf(title: string): string {
  const trimmed = title.trim();
  const initials = trimmed
    .split(/\s+/)
    .map((word) => [...word].find((ch) => MONOGRAM_CHAR.test(ch)))
    .filter((ch): ch is string => ch !== undefined)
    .slice(0, 2);

  // A title that is nothing but an emoji still has to put something on the face.
  return (initials.length > 0 ? initials : [...trimmed].slice(0, 1)).join('').toUpperCase();
}

/**
 * «12 товаров».
 *
 * Russian counts three ways, and a bare numeral on a tile says nothing about
 * what has been counted. «12 товар» is the tell of a shop nobody who speaks the
 * language has read.
 */
function itemCountLabel(count: number): string {
  const teens = count % 100;
  const last = count % 10;
  const noun =
    teens >= 11 && teens <= 14
      ? 'товаров'
      : last === 1
        ? 'товар'
        : last >= 2 && last <= 4
          ? 'товара'
          : 'товаров';

  // A non-breaking space: in a tile three words wide the numeral must not be
  // left hanging at the end of a line with its noun on the next one.
  return `${count}\u00A0${noun}`;
}

/*
 * The mark's geometry lives here rather than in `base.css` for the same reason
 * the button's spinner does: it is what keeps a stranger's image from breaking
 * the tile, which is a correctness property of this component and not a theme
 * choice. Face, size, radius and colour remain the stylesheet's.
 *
 * The monogram stays in the flow and holds the box open, so the tile is the
 * same height before the image arrives, after it arrives, and in the case where
 * it never does — nothing in the grid can reflow. The image is laid *over* the
 * letter and only revealed once the browser has decoded it, which is also why a
 * URL that 404s shows a monogram instead of a broken-image glyph.
 *
 * `visibility` rather than `opacity` or unmounting, on both layers: a brand mark
 * is usually transparent, and a letter showing through a logo is worse than
 * either state on its own.
 */
const MARK_BOX: CSSProperties = { position: 'relative' };
const MARK_HIDDEN: CSSProperties = { visibility: 'hidden' };
const MARK_IMAGE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  /*
   * `contain`, never `cover`. A brand mark cropped to fill a square is a
   * different mark, and a photo pasted at the wrong aspect has to letterbox on
   * the face rather than stretch — the two failures the owner would see first.
   */
  objectFit: 'contain',
};
const MARK_IMAGE_LOADING: CSSProperties = { ...MARK_IMAGE, visibility: 'hidden' };

export interface ServiceTileProps {
  readonly title: string;
  /**
   * A URL the owner pasted onto the category row. Image storage is deliberately
   * unconfigured, so `null` is the ordinary case here and not a failure.
   */
  readonly imageUrl?: string | null;
  /** How many products sit behind the service. Absent, null or zero prints no
      line at all: «0 товаров» is worse than saying nothing. */
  readonly count?: number | null;
  readonly onClick: () => void;
}

/**
 * One service in the home grid.
 *
 * The accessible name is the service name — the mark is decoration and is
 * hidden from the reader, or every tile would announce a letter before it
 * announced what it sells.
 */
export function ServiceTile({ title, imageUrl, count, onClick }: ServiceTileProps) {
  /*
   * The URL that loaded and the URL that failed, rather than two booleans: the
   * verdict then invalidates itself the moment the prop changes, so a tile that
   * React recycles for a different service cannot inherit the last one's.
   */
  const [loaded, setLoaded] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  // An empty string is not a URL: `src=""` resolves against the page and asks
  // the server for the app itself, once per tile.
  const src = imageUrl?.trim() || null;
  const shown = src !== null && failed !== src ? src : null;
  const imageUp = shown !== null && loaded === shown;

  const counted =
    typeof count === 'number' && Number.isFinite(count) && count >= 1
      ? itemCountLabel(Math.floor(count))
      : null;

  return (
    <button type="button" className="service-tile" onClick={onClick}>
      <span className="service-tile__mark" style={MARK_BOX} aria-hidden="true">
        <span className="service-tile__monogram" style={imageUp ? MARK_HIDDEN : undefined}>
          {monogramOf(title)}
        </span>
        {shown !== null ? (
          <img
            className="service-tile__image"
            src={shown}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
            /* The shop's own address is no business of whatever host the owner
               pasted a logo from. */
            referrerPolicy="no-referrer"
            style={imageUp ? MARK_IMAGE : MARK_IMAGE_LOADING}
            ref={(node) => {
              /*
               * An image already in the cache can finish before React attaches
               * the handlers below, and then neither of them ever fires and a
               * logo the customer has already seen stays hidden behind its
               * letter. `complete` is the browser's answer to both questions;
               * `naturalWidth` is what separates decoded from broken.
               */
              if (!node?.complete) return;
              if (node.naturalWidth > 0) setLoaded(shown);
              else setFailed(shown);
            }}
            onLoad={() => setLoaded(shown)}
            onError={() => setFailed(shown)}
          />
        ) : null}
      </span>
      <span className="service-tile__title">{title}</span>
      {counted ? <span className="service-tile__count">{counted}</span> : null}
    </button>
  );
}
